<template>
  <div v-if="isAuctionStarted" :class="['timeline', { winning: isLeader, flashing: isFlashing }]">
    <div class="timeline__label">
      <span>{{ bidStatusMessage }}</span>
      {{ timelineLabel }}
    </div>
    <div class="timeline__time-left" v-if="biddingDuration > 0">
      <div class="time-left__background-line">
        <span class="progress" :style="{ width: progressWidth + '%' }"></span>
      </div>
      <div class="time-left__time">
        {{ remainingTime > 0 ? remainingTime : "" }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch, inject } from "vue";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { LotSoldStatuses, LotStatuses, BiddingSteps } from "@/enum";
  import { trans } from "@/utils/trans";

  const auctionStore = useAuctionStore();
  const emitter = inject("emitter");
  const liveSettings = computed(() => auctionStore.liveSettings);
  const lots = computed(() => auctionStore.lots);
  const liveLot = computed(() => lots.value.find((item) => item.status === LotStatuses.LIVE));
  const endedLots = computed(() => lots.value.filter((item) => item.status === LotStatuses.ENDED));
  const isAuctionStarted = computed(() => liveLot.value?.id || endedLots.value.length);

  const activeLot = computed(() => auctionStore.activeLot);
  const soldStatus = computed(() => activeLot.value?.sold_status);
  const userId = computed(() => auctionStore.user?.id);
  const userBid = computed(() => (activeLot.value?.id ? auctionStore.user?.bids[activeLot.value.id] ?? 0 : 0));
  const currentStep = computed(() => (auctionStore.activeLot?.id ? auctionStore.activeLot.live_step : 0));

  const leaderId = computed(() => activeLot.value?.leader_id);
  const isLeader = computed(() => userId.value === leaderId.value);
  const isLotCompleted = computed(
    () =>
      activeLot.value?.sold_status === LotSoldStatuses.SOLD || activeLot.value?.sold_status === LotSoldStatuses.UNSOLD
  );

  let interval: number | undefined;
  const remainingTime = ref(0);
  const progressWidth = ref(100);
  const biddingDuration = computed(() => auctionStore.liveSettings?.bidding_duration ?? 10);
  const flashingDuration = computed(() => auctionStore.liveSettings?.warning_flashing_duration ?? 3);
  const isFlashing = ref(false);

  const bidStatusMessage = computed(() => {
    if (userBid.value > 0 && !auctionStore.user?.is_admin) {
      if (isLeader.value) {
        return isLotCompleted.value ? trans("live.bidding.you_won") : trans("live.bidding.leading");
      } else {
        return isLotCompleted.value ? "" : trans("live.bidding.outbid");
      }
    } else {
      if (currentStep.value === BiddingSteps.COMPLETE_LOT || isLotCompleted.value) {
        return "";
      }

      if (auctionStore.liveSettings?.next_lot_message) {
        return auctionStore.liveSettings.next_lot_message;
      } else {
        return trans("live.bidding.place_your_bids");
      }
    }
  });

  const timelineLabel = computed(() => {
    switch (currentStep.value) {
      case BiddingSteps.ONCE:
        return liveSettings.value?.once_message ?? "Once";
      case BiddingSteps.TWICE:
        return liveSettings.value?.twice_message ?? "Twice";
      case BiddingSteps.COMPLETE_LOT:
        return soldStatus.value === LotSoldStatuses.SOLD || soldStatus.value === LotSoldStatuses.UNSOLD
          ? trans("live.bidding.lot_" + soldStatus.value.toLowerCase())
          : "";
      default:
        return "";
    }
  });

  watch(
    () => auctionStore.resetTimeLineTrigger,
    () => {
      if (biddingDuration.value) {
        startCountdown();
      }
    }
  );

  watch(
    () => activeLot.value?.sold_status,
    (newValue) => {
      if (newValue === LotSoldStatuses.SOLD || newValue === LotSoldStatuses.UNSOLD) {
        isFlashing.value = false;
        stopCountdown();
      }
    }
  );

  const startCountdown = () => {
    isFlashing.value = false;
    if (interval) {
      clearInterval(interval);
    }

    const duration = biddingDuration.value;
    remainingTime.value = duration;
    progressWidth.value = 100;

    const startTime = Date.now();
    interval = setInterval(() => {
      const elapsedTime = (Date.now() - startTime) / 1000;
      remainingTime.value = Math.max(duration - Math.floor(elapsedTime), 0);
      progressWidth.value = Math.min((elapsedTime / duration) * 100, 100);

      if (flashingDuration.value > 0 && flashingDuration.value >= remainingTime.value) {
        isFlashing.value = true;
      }

      if (elapsedTime >= duration) {
        clearInterval(interval);
      }
    }, 10);
  };

  const stopCountdown = () => {
    if (interval) {
      clearInterval(interval);
      interval = undefined;
    }

    remainingTime.value = 0;
    progressWidth.value = 100;
    isFlashing.value = false;
  };
</script>

<style lang="scss" scoped>
  .timeline.flashing .timeline__label {
    animation: flashing 0.3s infinite alternate;
  }

  @keyframes flashing {
    from {
      opacity: 1;
    }
    to {
      opacity: 0.3;
    }
  }
</style>
